import React, { useState, useEffect, useCallback, useMemo } from "react";
import { DataGrid } from '@mui/x-data-grid';
import { useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faTrash } from "@fortawesome/free-solid-svg-icons";
import DeleteConfirmation from "./../../components/DeleteConfirmation.js";
import { useHeader } from './../../components/HeaderContext';
import { useAxiosPrivate } from "./../../hooks/useAxiosPrivate.js";
import { encryptId } from './../../components/utils_react.jsx';
import useAuth from "./../../hooks/useAuth.js";
import OkModal from "./../../components/OkModal.js";
import { useAlert } from './../../components/AlertContext.jsx';
import dayjs from 'dayjs';
import "./../../App.css";

const TestOrders = () => {
    // Constants and hooks
    const baseURL = process.env.REACT_APP_API_BASE_URL;
    const axiosPrivate = useAxiosPrivate();
    const { auth } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const { setHeader } = useHeader();
    const { triggerAlert } = useAlert();
    const ReturnPage = "/test_orders";

    // State management
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [totalRows, setTotalRows] = useState(0);
    const [showOkModal, setShowOkModal] = useState(false);
    const [okMessage, setOkMessage] = useState("");
    const [typeOkModal, setTypeOkModal] = useState("success");

    // Important: Use a ref to ensure we always have the latest pagination state
    const paginationRef = React.useRef({
        page: location.state?.pagination?.page || 0,
        pageSize: location.state?.pagination?.pageSize || 20,
        sortField: location.state?.pagination?.sortField || "test_order_no",
        sortOrder: location.state?.pagination?.sortOrder || "asc"
    });

    // Pagination state - initialize from location state if available
    const [pagination, setPagination] = useState(paginationRef.current);

    // Update ref whenever pagination state changes
    useEffect(() => {
        paginationRef.current = pagination;
    }, [pagination]);

    // Set header title
    useEffect(() => {
        setHeader("Ordens de Análise");
    }, [setHeader]);

    const [type, setType] = useState(null);
    const [id, setId] = useState(null);         // usado no dialogo de confirmar delete e voltar
    const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false);
    const [deleteMessage, setDeleteMessage] = useState(null);

    // Handle the displaying of the modal based on type and id
    const showDeleteModal = (type, id) => {
        setId(id);
        console.log(id);
        setDeleteMessage(`Are you sure you want to delete the record: ${type}?`);

        setDisplayConfirmationModal(true);
    };

    // Hide the modal
    const hideConfirmationModal = () => {
        setDisplayConfirmationModal(false);
    };

    const handleShowOkModal = (message, type) => {
        setOkMessage(message);
        setTypeOkModal(type);
        setShowOkModal(true);
    }

    const handleHideOkModal = () => {
        setShowOkModal(false);
    };

    // Memorize columns to prevent unnecessary re-renders
    const columns = useMemo(() => [
        {
            field: 'actions',
            headerName: '',
            disableColumnMenu: true,
            width: 60,
            renderCell: (params) => (
                <div>
                    <FontAwesomeIcon
                        icon={faEye}
                        style={{ cursor: 'pointer', marginRight: '8px' }}
                        onClick={() => ViewRow(params.row.id)}
                    />
                    {/*
                    <FontAwesomeIcon
                        icon={faTrash}
                        style={{ cursor: 'pointer' }}
                        onClick={() => deleteRow(params.row.test_order_no, params.row.id)}
                    />
                    */}
                </div>
            ),
        },
        { field: 'test_order_header_id', headerName: 'ID', disableColumnMenu: true },
        { field: 'test_order_no', headerName: 'Ordem Análise', width: 120, headerClassName: 'super-app-theme--header', disableColumnMenu: true },
        { field: 'description', headerName: 'Descrição', width: 300, disableColumnMenu: true },

        {
            field: 'source_type',
            headerName: 'Origem Tipo',
            width: 150,
            disableColumnMenu: true,
            renderCell: (params) => {
                const state = options_source_type.find(option => option.value === params.value);
                return <span>{state ? state.label : params.value}</span>;
            }
        },

        { field: 'source_no', headerName: 'Origem No', width: 150, disableColumnMenu: true },
        { field: 'source_line_no', headerName: 'Origem Linha', width: 100, disableColumnMenu: true },
        { field: 'test_date', headerName: 'Data Teste', width: 100, valueFormatter: test_date => test_date ? dayjs(test_date).format('YYYY-MM-DD') : '', disableColumnMenu: true },
        {
            field: 'status',
            headerName: 'Estado',
            width: 140,
            disableColumnMenu: true,
            renderCell: (params) => {
                const state = options_status.find(option => option.value === params.value);
                return <span>{state ? state.label : params.value}</span>;
            }
        },
    ], []);


    const options_source_type = [
        { value: 0, label: 'Inventário' },
        { value: 1, label: 'Enc Compra' },
        { value: 2, label: 'Recep Compra' },
        { value: 3, label: 'Enc Venda' },
        { value: 4, label: 'Envio Venda' },
        { value: 5, label: 'Enc Devol Venda' },
        { value: 6, label: 'Recep Devol Venda' },
        { value: 7, label: 'Ordem Produção' },
        { value: 8, label: 'Operação' },
        { value: 9, label: 'Estabilidade' },
        { value: 10, label: '' },
        { value: 11, label: 'Componente' },
        { value: 12, label: '' },
        { value: 13, label: '' },
        { value: 14, label: '' },
        { value: 15, label: 'Ordem Volante' },
        { value: 16, label: 'Ficha Tec Seamless' },
        { value: 17, label: 'Ordem Controlo' }
    ]

    const options_status = [
        { value: 0, label: 'Criado' },
        { value: 1, label: '' },
        { value: 2, label: '' },
        { value: 3, label: 'A Registar Valor' },
        { value: 4, label: '' },
        { value: 5, label: 'Teste Concluído' },
        { value: 6, label: '' },
        { value: 7, label: '' },
        { value: 8, label: 'Concluído' },
        { value: 9, label: '' },
        { value: 10, label: '' },
        { value: 11, label: 'Cancelado' }
    ]




    const ViewRow = (param1) => {
        // Always use the ref for the most up-to-date pagination values
        const currentPagination = paginationRef.current;

        console.log("ViewRow. Pagination.page: ", currentPagination.page);
        const encryptedId = encryptId(param1);

        navigate(`/test_orders_edit/${encryptedId}`, {
            state: {
                pagination: {
                    page: currentPagination.page,
                    pageSize: currentPagination.pageSize,
                    sortField: currentPagination.sortField,
                    sortOrder: currentPagination.sortOrder
                },
                from: 'test_orders'
            }
        });
    };

    useEffect(() => {
        console.log("Pagination state updated:", pagination);
    }, [pagination]);

    function deleteRow(param1, param2) {
        console.log("test_orders.js > deleteRow > param1: ", param1, "param2: ", param2);
        showDeleteModal(param1, param2)
    }

    const paginationModel = {
        page: pagination.page,
        pageSize: pagination.pageSize
    };

    // Use useCallback to memoize fetch function
    const fetchTestOrders = useCallback(async () => {
        try {
            setLoading(true);
            const result = await axiosPrivate.get(
                `${baseURL}/test_orders_page?page=${pagination.page + 1}&limit=${pagination.pageSize}&sortfield=${pagination.sortField}&sortorder=${pagination.sortOrder}`
            );

            if (result.status === 200) {
                setRows(result.data.result.map(row => ({ id: row.test_order_header_id, ...row })));
                setTotalRows(result.data.total_count);
            } else {
                triggerAlert(result.data.Error, 'error');
            }
        } catch (error) {
            console.error('Failed to fetch test orders:', error);
            triggerAlert('Failed to fetch test orders', 'error');
        } finally {
            setLoading(false);
        }
    }, [pagination, axiosPrivate, baseURL, triggerAlert]);

    const handleSubmitDelete = async (id) => {
        console.log(id);
        try {
            setDisplayConfirmationModal(false);
            const res1 = await axiosPrivate.get(`${baseURL}/test_orders_allowdelete/` + id);
            let total_count = res1.data.total_count;
            console.log("total_count: ", total_count);

            if (total_count >= 1) {
                handleShowOkModal("Test Order in use. Record can't be deleted!", "warning");
            } else {
                try {
                    const res2 = await axiosPrivate.delete(`${baseURL}/test_orders/` + id);
                    let message = `Record deleted!`;
                    triggerAlert(message, 'success');
                    console.log(res2);
                    fetchTestOrders();
                } catch (err) {
                    console.log('Error fetching data:', err);
                    console.log(err.response.data.err.originalError.info.message);
                    let message = `Erro na gravação!  \n ${err.response.data.err.originalError.info.message}`;
                    triggerAlert(message, 'error');
                }
            }
        } catch (err) {
            console.log('Error fetching data:', err);
            let message = `Erro na gravação!  \n ${err.response.data.err.originalError.info.message}`;
            triggerAlert(message, 'error');
        }
    };

    // Effect to restore pagination state from location
    useEffect(() => {
        if (location.state?.pagination) {
            const newPagination = {
                page: location.state.pagination.page,
                pageSize: location.state.pagination.pageSize,
                sortField: location.state.pagination.sortField || "test_order_no",
                sortOrder: location.state.pagination.sortOrder || "asc"
            };

            setPagination(newPagination);
            // Also update the ref to keep them in sync
            paginationRef.current = newPagination;
        }
    }, [location.state]);

    // Effect to load test orders when pagination changes
    useEffect(() => {
        const controller = new AbortController();
        fetchTestOrders();
        return () => controller.abort();
    }, [fetchTestOrders]);

    // Update handlePageChange to keep everything in sync
    const handlePaginationChange = useCallback((newPagination) => {
        setPagination(prev => ({
            ...prev,
            ...newPagination
        }));
    }, []);

    // Handler for sorting
    const handleSortModelChange = (newSortModel) => {
        if (newSortModel.length > 0) {
            const { field, sort } = newSortModel[0];
            const newPagination = {
                ...pagination,
                page: 0,  // Reset to first page when sorting changes
                sortField: field,
                sortOrder: sort
            };

            setPagination(newPagination);
            paginationRef.current = newPagination;
        } else {
            const newPagination = {
                ...pagination,
                sortField: "test_order_no",
                sortOrder: "asc"
            };

            setPagination(newPagination);
            paginationRef.current = newPagination;
        }
    };

    const handleBtnNew = async () => {
        console.log("test_orders.js > handleBtnNew ");
        navigate(`/test_orders_add`);
    };

    // Add this useEffect to log state changes
    useEffect(() => {
        console.log("Current pagination state:", pagination);
    }, [pagination]);

    return (
        <div className="mt-2">
            <div className="row w-100 mb-2">
                <div className="col-1 d-flex flex-column justify-content-end">
                    {/*
                    <div className="mt-auto">
                        <button type="button" className="btn btn-outline-primary btn-sm mb-2" id="abrir" onClick={handleBtnNew}>+ New</button>
                    </div>
                    */}
                </div>
            </div>

            <div className="custom-data-grid" style={{ width: '90%', position: 'relative' }}>
                <DataGrid
                    slotProps={{
                        pagination: {
                            showFirstButton: true,
                            showLastButton: true,
                        },
                    }}
                    rows={rows}
                    columns={columns}
                    rowHeight={40}
                    disableRowSelectionOnClick={true}
                    autoHeight={true}
                    paginationMode="server"
                    sortingMode="server"
                    //density="compact"
                    rowCount={totalRows}
                    pagination
                    pageSizeOptions={[20, 40, 60]}
                    onCellClick={(params) => {
                        console.log("===cell click===", params);
                    }}
                    paginationModel={paginationModel}
                    onPaginationModelChange={(model) => {
                        console.log("Pagination changed:", model);
                        const newPagination = {
                            ...pagination,
                            page: model.page,
                            pageSize: model.pageSize
                        };

                        setPagination(newPagination);
                        // Update the ref immediately to ensure ViewRow gets the latest value
                        paginationRef.current = newPagination;
                    }}
                    initialState={{
                        pagination: { paginationModel: { page: 0, pageSize: 20 } },
                        // Hide column test_order_header_id, the other columns will remain visible
                        columns: { columnVisibilityModel: { test_order_header_id: false } },
                    }}
                    onSortModelChange={handleSortModelChange}
                    loading={loading}
                    disableColumnFilter
                    disableColumnSelector
                    disableDensitySelector
                />
                <DeleteConfirmation
                    showModal={displayConfirmationModal}
                    confirmModal={() => handleSubmitDelete(id)}
                    hideModal={hideConfirmationModal}
                    type={type}
                    id={id}
                    message={deleteMessage}
                />

                <OkModal
                    showModal={showOkModal}
                    hideModal={handleHideOkModal}
                    message={okMessage}
                    type={typeOkModal}
                    header=""
                />

                {loading && (
                    <div className="overlay">
                        <div className="loading-circle"></div>
                    </div>
                )}
            </div>
        </div>
    );
}

export default TestOrders;